import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PM_SYSTEMS } from "@/data/content";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { TextField, SelectField, TextareaField } from "@/components/consultation/fields";

const EMPTY = {
    practice_name: "",
    contact_name: "",
    email: "",
    phone: "",
    num_providers: "",
    pm_system: "",
    message: "",
};

function SuccessCard({ dark, onReset }) {
    return (
        <div
            data-testid="consultation-success"
            className={`flex flex-col items-start gap-4 py-10 ${dark ? "text-cream" : "text-ink"}`}
        >
            <CheckCircle2 size={40} className="text-terracotta" />
            <h3 className="font-heading text-3xl">Thank you.</h3>
            <p className={dark ? "text-cream/70 max-w-md" : "text-inksoft max-w-md"}>
                We've received your request and a U.S.-based specialist will reach out to walk you
                through exactly what we can take off your plate.
            </p>
            <button
                onClick={onReset}
                data-testid="submit-another-button"
                className="mt-2 font-mono text-xs uppercase tracking-[0.18em] underline underline-offset-4 hover:text-terracotta transition-colors"
            >
                Submit another request
            </button>
        </div>
    );
}

export function ConsultationForm({ variant = "light" }) {
    const [form, setForm] = useState(EMPTY);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const dark = variant === "dark";
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const inputBase =
        "w-full bg-transparent border-b py-3 text-base outline-none transition-colors duration-200 placeholder:text-current/40 focus:border-terracotta";
    const inputCls = dark
        ? `${inputBase} border-cream/25 text-cream placeholder:text-cream/40`
        : `${inputBase} border-linec text-ink`;
    const labelCls = `font-mono text-[11px] uppercase tracking-[0.18em] mb-1 block ${
        dark ? "text-cream/60" : "text-inksoft"
    }`;

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/leads", form);
            setDone(true);
            setForm(EMPTY);
            toast.success("Request received. We'll be in touch shortly.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) return <SuccessCard dark={dark} onReset={() => setDone(false)} />;

    const fieldProps = { labelCls, inputCls };
    const selectCls = `${inputCls} appearance-none cursor-pointer ${dark ? "[&>option]:text-ink" : ""}`;

    return (
        <form onSubmit={submit} data-testid="consultation-form" className="grid gap-6">
            <div className="grid sm:grid-cols-2 gap-6">
                <TextField {...fieldProps} label="Practice name" testId="input-practice-name" required placeholder="Bright Smile Dental" value={form.practice_name} onChange={set("practice_name")} />
                <TextField {...fieldProps} label="Contact name" testId="input-contact-name" required placeholder="Jane Doe" value={form.contact_name} onChange={set("contact_name")} />
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
                <TextField {...fieldProps} label="Email" testId="input-email" required type="email" placeholder="jane@practice.com" value={form.email} onChange={set("email")} />
                <TextField {...fieldProps} label="Phone" testId="input-phone" required placeholder="(555) 123-4567" value={form.phone} onChange={set("phone")} />
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
                <TextField {...fieldProps} label="Number of providers" testId="input-num-providers" placeholder="e.g. 3" value={form.num_providers} onChange={set("num_providers")} />
                <SelectField label="Current PM system" labelCls={labelCls} inputCls={selectCls} testId="input-pm-system" options={PM_SYSTEMS} value={form.pm_system} onChange={set("pm_system")} />
            </div>
            <TextareaField {...fieldProps} label="Message" testId="input-message" inputCls={`${inputCls} resize-none`} rows={3} placeholder="Tell us about your current billing setup…" value={form.message} onChange={set("message")} />

            <button
                type="submit"
                disabled={submitting}
                data-testid="submit-consultation-button"
                className="inline-flex items-center justify-center gap-2 bg-terracotta text-cream px-8 py-4 rounded-sm font-medium tracking-tight transition-[background-color,transform] duration-200 hover:bg-terracottadark hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 w-full sm:w-auto"
            >
                {submitting && <Loader2 size={16} className="animate-spin" />}
                {submitting ? "Sending…" : "Request a Consultation"}
            </button>
        </form>
    );
}
